import { AutoAwesomeOutlined, LockOutlined, SchoolOutlined } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import LogoImg from 'assets/icons/logo.svg';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface AuthSplitShellProps {
  children: ReactNode;
}

const AuthSplitShell = ({ children }: AuthSplitShellProps) => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <SchoolOutlined sx={{ fontSize: 24 }} />,
      text: t('loginPage.heroFeatures.questionFit'),
    },
    {
      icon: <AutoAwesomeOutlined sx={{ fontSize: 24 }} />,
      text: t('loginPage.heroFeatures.aiEvaluation'),
    },
    {
      icon: <LockOutlined sx={{ fontSize: 24 }} />,
      text: t('loginPage.heroFeatures.result'),
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        flexGrow: 1,
      }}>
      <Box
        sx={{
          width: '100%',
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '44% 56%' },
          minHeight: '100%',
        }}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            color: 'common.white',
            px: { xs: 3, sm: 5, md: 6.5 },
            py: { xs: 4, md: 6 },
            background: 'linear-gradient(180deg, #2A3170 0%, #2F52DF 68%, #3B5BF1 100%)',
          }}>
          <Box
            sx={{
              position: 'absolute',
              top: { xs: -80, md: -60 },
              right: { xs: -120, md: -90 },
              width: { xs: 240, md: 420 },
              height: { xs: 240, md: 420 },
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.08)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: -110, md: -120 },
              left: { xs: -90, md: -110 },
              width: { xs: 240, md: 320 },
              height: { xs: 240, md: 320 },
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.08)',
            }}
          />

          <Stack
            spacing={{ xs: 3, md: 5 }}
            justifyContent='center'
            sx={{ position: 'relative', zIndex: 1, height: '100%', width: '100%' }}>
            <Box
              component='img'
              src={LogoImg}
              alt='MyHRBox'
              sx={{
                width: { xs: 150, sm: 172 },
                height: 'auto',
              }}
            />

            <Stack spacing={2.5}>
              <Typography
                sx={{
                  fontSize: { xs: 32, md: 48 },
                  lineHeight: { xs: 1.15, md: 1.08 },
                  fontWeight: 700,
                  color: 'white',
                }}>
                {t('loginPage.heroTitle')}
              </Typography>

              <Typography
                variant='body16rg'
                sx={{
                  color: 'white',
                  fontSize: { xs: 18, md: 20 },
                  lineHeight: 1.65,
                  maxWidth: 500,
                }}>
                {t('loginPage.heroDescription')}
              </Typography>
            </Stack>

            <Stack spacing={2}>
              {features.map((feature) => (
                <Stack key={feature.text} direction='row' spacing={2} alignItems='center'>
                  <Box
                    sx={{
                      minWidth: 50,
                      width: 50,
                      height: 50,
                      borderRadius: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255,255,255,0.12)',
                    }}>
                    {feature.icon}
                  </Box>

                  <Typography
                    variant='body16rg'
                    sx={{ color: 'common.white', fontSize: { xs: 18, md: 20 }, lineHeight: 1.4 }}>
                    {feature.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            backgroundColor: 'common.white',
            display: 'flex',
            minHeight: '100%',
            justifyContent: 'center',
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 4, md: 6 },
          }}>
          <Stack
            spacing={4}
            sx={{
              width: '100%',
              maxWidth: 664,
              justifyContent: 'center',
            }}>
            {/* {!shouldHideTabs && <AuthTabs pathname={pathname} />} */}
            <Box>{children}</Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthSplitShell;
